// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/home/QuickActions.tsx
// 🔧 PURPOSE: Le tessere della card del soggiorno (tre, o due) e i pannelli
//             che aprono sotto di sé.
//
//             Quali tessere, se la vista non le sceglie (`items`):
//             - soggiorno: Codice porta, WiFi, Reception 24h;
//             - prima dell'arrivo: Come arrivare (se c'è l'indirizzo), Il
//               mio soggiorno, Reception 24h; il giorno dell'arrivo al
//               posto di "Il mio soggiorno" c'è il Codice porta (se il
//               check-in è fatto);
//             - dopo la partenza: nessuna (niente codice, niente WiFi).
//
//             Le regole su casa e codice sono quelle di homeState (le stesse
//             di MyStay): il codice solo a check-in fatto e mai dopo la
//             partenza, il WiFi dal giorno dell'arrivo a quello della
//             partenza. Prima del check-in le due tessere dicono "Dopo il
//             check-in" e portano al check-in (dei gruppi, se è un gruppo).
//             "Reception 24h" apre la scelta Assistente AI / reception
//             (HomeSessionHost), con il pallino dei non letti.
// ============================================

import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Briefcase, KeyRound, MapPin, MessageCircle, Wifi, type LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { HomeState } from '../../lib/homeState';
import type { GuestBooking } from '../../lib/guestApi';
import { copyWifiPassword } from '../../config/wifi';
import { hasDirections, openDirections } from '../../lib/directions';
import { useDoorCode } from '../../hooks/useDoorCode';
import { useHomeSession } from './HomeSessionHost';
import DoorCodeReveal from './DoorCodeReveal';
import { FOCUS_RING, IconCircle, INK_TEXT, UnreadBadge } from './ui';

export type QuickActionKey = 'door' | 'wifi' | 'reception' | 'directions' | 'myStay';

// Le indicazioni stanno in lib/directions (le usa anche "Adesso per te"):
// qui si riesportano per chi le prendeva da questo file.
export { openDirections };

type QuickBooking = Pick<
  GuestBooking,
  'guestEmail' | 'checkOut' | 'apartmentName' | 'apartmentAddress' | 'apartmentLatitude' | 'apartmentLongitude'
>;

/** Le tessere di una vista, dalle regole in testa al file. */
export function defaultQuickActions(state: HomeState, booking: Partial<QuickBooking> | null | undefined): QuickActionKey[] {
  const hasAddress = hasDirections(booking);
  if (state.view === 'stay') return ['door', 'wifi', 'reception'];
  if (state.view === 'before') {
    const middle: QuickActionKey = state.isArrivalDay && state.doorCodeAvailable ? 'door' : 'myStay';
    return [...(hasAddress ? (['directions'] as QuickActionKey[]) : []), middle, 'reception'];
  }
  return [];
}

export interface QuickActionsProps {
  state: HomeState;
  token: string | null;
  booking: Partial<QuickBooking> | null;
  /** Le tessere da mostrare, al posto di quelle decise dallo stato. */
  items?: QuickActionKey[];
  className?: string;
}

const COPIED_MS = 2000;

const TILE =
  'relative flex min-h-[96px] w-full flex-col items-start gap-1.5 rounded-[14px] border border-nva-edge bg-nva-bone px-2 py-2.5 text-left ' +
  'transition-colors hover:bg-nva-cream/60 dark:border-stone-700 dark:bg-stone-900 dark:hover:bg-stone-900/70 motion-safe:active:scale-[0.98]';
const TILE_LABEL = 'text-[12px] font-bold leading-tight text-nva-muted dark:text-stone-400';
const TILE_VALUE = 'text-[13.5px] font-extrabold leading-tight';

interface TileProps {
  icon: LucideIcon;
  label?: string;
  value: React.ReactNode;
  badge?: React.ReactNode;
  onClick?: () => void;
  to?: string;
  expanded?: boolean;
  controls?: string;
  ariaLabel?: string;
}

const Tile: React.FC<TileProps> = ({ icon: Icon, label, value, badge, onClick, to, expanded, controls, ariaLabel }) => {
  // Il pallino sta ACCANTO al cerchio, non dentro: il cerchio è aria-hidden
  // e il testo dei non letti (sr-only) non arriverebbe ai lettori di schermo.
  const body = (
    <>
      <span className="relative flex-shrink-0">
        <IconCircle tone="ink" size={34}>
          <Icon />
        </IconCircle>
        {badge}
      </span>
      {label ? <span className={TILE_LABEL}>{label}</span> : null}
      <span className={cn(TILE_VALUE, INK_TEXT)}>{value}</span>
    </>
  );
  if (to) {
    return (
      <Link to={to} className={cn(TILE, FOCUS_RING)} aria-label={ariaLabel}>
        {body}
      </Link>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(TILE, FOCUS_RING)}
      aria-expanded={expanded}
      aria-controls={controls}
      aria-label={ariaLabel}
    >
      {body}
    </button>
  );
};

const QuickActions: React.FC<QuickActionsProps> = ({ state, token, booking, items, className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openReception, unreadCount } = useHomeSession();
  const door = useDoorCode(token, booking ? { guestEmail: booking.guestEmail || '', checkOut: booking.checkOut || '' } : null);
  const [doorAsk, setDoorAsk] = useState(false);
  const [wifiCopied, setWifiCopied] = useState(false);
  const wifiTimer = useRef<number | null>(null);

  const keys = (items || defaultQuickActions(state, booking)).filter((key) => {
    // Dall'orario di check-out (più l'ora di margine) la casa non si comanda
    // più, qualunque cosa chieda la vista.
    if ((key === 'door' || key === 'wifi') && state.stayOver) return false;
    // Check-in fatto ma codice/WiFi non ancora disponibili: la tessera non serve.
    if (key === 'door' && state.checkInDone && !state.doorCodeAvailable) return false;
    if (key === 'wifi' && state.checkInDone && !state.homeAvailable) return false;
    return true;
  });

  // Il pannello dell'email si chiude quando il codice arriva o sparisce.
  useEffect(() => { setDoorAsk(false); }, [door.revealed, token]);
  useEffect(() => () => {
    if (wifiTimer.current !== null) window.clearTimeout(wifiTimer.current);
  }, []);

  if (keys.length === 0) return null;

  // "Dopo il check-in": al check-in online finché si può fare; dal secondo
  // giorno dopo l'arrivo il channel manager non lo accetta più
  // (out_of_window) e lo sistema la reception.
  const toCheckIn = () => {
    if (state.checkInWindowOpen) navigate(state.checkInPath);
    else openReception();
  };

  const panelId = 'home-door-panel';
  const doorOpen = doorAsk || door.revealed;

  const copyWifi = async () => {
    const ok = await copyWifiPassword();
    if (!ok) return;
    setWifiCopied(true);
    if (wifiTimer.current !== null) window.clearTimeout(wifiTimer.current);
    wifiTimer.current = window.setTimeout(() => setWifiCopied(false), COPIED_MS);
  };

  const tiles = keys.map((key) => {
    switch (key) {
      case 'door':
        if (!state.doorCodeAvailable) {
          return (
            <Tile
              key={key}
              icon={KeyRound}
              label={t('home.quick.door')}
              value={t('home.quick.doorNotYet')}
              onClick={toCheckIn}
            />
          );
        }
        return (
          <Tile
            key={key}
            icon={KeyRound}
            label={t('home.quick.door')}
            value={door.revealed
              ? (door.apartments ? t('home.quick.doorHide') : <span className="tracking-[0.12em]">{door.code}</span>)
              : t('home.quick.doorShow')}
            expanded={doorOpen}
            controls={panelId}
            onClick={() => {
              if (door.revealed) door.hide();
              else setDoorAsk((open) => !open);
            }}
          />
        );
      case 'wifi':
        if (!state.homeAvailable) {
          return (
            <Tile
              key={key}
              icon={Wifi}
              label={t('myStay.home.wifi')}
              value={t('home.quick.doorNotYet')}
              onClick={toCheckIn}
            />
          );
        }
        return (
          <Tile
            key={key}
            icon={Wifi}
            label={t('myStay.home.wifi')}
            value={wifiCopied ? t('home.quick.wifiCopied') : t('home.quick.wifiCopy')}
            onClick={() => void copyWifi()}
          />
        );
      case 'reception': {
        const unreadLabel = t('home.reception.unread', { count: unreadCount });
        return (
          <Tile
            key={key}
            icon={MessageCircle}
            label={t('home.quick.reception')}
            value={t('home.quick.write')}
            onClick={openReception}
            badge={<UnreadBadge count={unreadCount} label={unreadLabel} className="absolute -right-2 -top-1.5" />}
          />
        );
      }
      case 'directions':
        return (
          <Tile
            key={key}
            icon={MapPin}
            value={t('home.quick.directions')}
            onClick={() => openDirections(booking?.apartmentAddress, booking?.apartmentLatitude, booking?.apartmentLongitude)}
          />
        );
      case 'myStay':
      default:
        return <Tile key={key} icon={Briefcase} value={t('home.quick.myStay')} to="/my-stay" />;
    }
  });

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div
        role="group"
        aria-label={t('home.quick.label')}
        className={cn('grid gap-2', keys.length === 1 ? 'grid-cols-1' : keys.length === 2 ? 'grid-cols-2' : 'grid-cols-3')}
      >
        {tiles}
      </div>
      <span className="sr-only" aria-live="polite">{wifiCopied ? t('home.quick.wifiCopied') : ''}</span>
      {keys.indexOf('door') >= 0 && state.doorCodeAvailable && doorOpen ? (
        <div id={panelId}>
          <DoorCodeReveal
            door={door}
            apartmentName={booking?.apartmentName}
            note={state.taxDue ? t('myStay.touristTax.codeAfterPayment') : undefined}
          />
        </div>
      ) : null}
    </div>
  );
};

export default QuickActions;
